import React, { useState, useEffect } from "react";
import { Modal, Upload, Button, Input, message } from "antd";
import { UploadOutlined, LoadingOutlined, SaveOutlined, CloseOutlined, BuildOutlined, PictureOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import AddImage from "../../Global Functions/addImage";
import { updateFloorPlanImage } from "../../Redux/Actions/rentalPropertiesActions";
import "./updateFloorPlanPopup.css";

const UpdateFloorPlanPopup = ({ isOpen, onClose, propertyId, existingData }) => {
    const dispatch = useDispatch();

    const [fileList, setFileList] = useState([]);
    const [label, setLabel] = useState("");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFileList(
                existingData?.floor_plan_image
                    ? [{
                        uid: "existing-floor-plan",
                        name: existingData.floor_plan_image.split("/").pop() || "floor-plan.jpg",
                        status: "done",
                        url: existingData.floor_plan_image,
                    }]
                    : []
            );
            setLabel(existingData?.floor_plan_label || "");
        }
    }, [isOpen, existingData]);

    if (!isOpen) return null;

    const currentUrl = fileList.find((f) => f.status === "done")?.url;

    const customUpload = async ({ file, onSuccess, onError }) => {
        try {
            setUploading(true);
            const sanitizedFile = new File([file], file.name.replace(/\s+/g, "_"), { type: file.type });
            const url = await AddImage(sanitizedFile);
            onSuccess({ url }, file);
            setFileList([{ uid: file.uid, name: file.name, status: "done", url }]);
        } catch (err) {
            message.error("Floor plan image upload failed!");
            onError(err);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setFileList([]);
        return true;
    };

    const handleSubmit = async () => {
        if (!currentUrl) {
            message.error("Please upload a floor plan image");
            return;
        }

        setSubmitting(true);
        try {
            const res = await dispatch(
                updateFloorPlanImage({
                    property_id: propertyId,
                    floor_plan_image: currentUrl,
                    floor_plan_label: label,
                })
            );
            if (res?.statusCode === 200) {
                onClose();
            }
        } catch {
            message.error("Failed to update floor plan");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={
                <div className="ufp-modal-header-content">
                    <BuildOutlined style={{ color: "white", fontSize: 16 }} />
                    <span style={{ color: "white" }}>Update Floor Plan</span>
                </div>
            }
            open={isOpen}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "white", fontSize: 16 }} />}
                    onClick={onClose}
                    className="ufp-close-btn"
                />
            }
            destroyOnClose
            width={480}
            centered
            className="ufp-modal-override"
            styles={{
                header: {
                    background: "#0D152B",
                    borderRadius: "16px 16px 0 0",
                    padding: "18px 24px",
                    borderBottom: "none",
                },
                content: { borderRadius: 16, overflow: "hidden", padding: 0 },
                body: { padding: "24px", background: "#ffffff" },
            }}
        >
            <div className="ufp-section">
                <p className="ufp-section-label">
                    <PictureOutlined /> Floor Plan Image
                </p>

                {currentUrl ? (
                    <div className="ufp-preview-wrap">
                        <img src={currentUrl} alt="Floor plan" className="ufp-preview-img" />
                        <div className="ufp-preview-overlay">
                            <Upload
                                customRequest={customUpload}
                                accept="image/*"
                                showUploadList={false}
                            >
                                <Button
                                    className="ufp-replace-btn"
                                    icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
                                >
                                    {uploading ? "Uploading…" : "Replace"}
                                </Button>
                            </Upload>
                            <Button
                                className="ufp-remove-btn"
                                icon={<CloseOutlined />}
                                onClick={handleRemove}
                            >
                                Remove
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Upload.Dragger
                        customRequest={customUpload}
                        accept="image/*"
                        showUploadList={false}
                        className="ufp-dropzone"
                    >
                        {uploading ? (
                            <div className="ufp-dropzone-content">
                                <LoadingOutlined className="ufp-dropzone-icon" />
                                <p className="ufp-dropzone-title">Uploading…</p>
                            </div>
                        ) : (
                            <div className="ufp-dropzone-content">
                                <UploadOutlined className="ufp-dropzone-icon" />
                                <p className="ufp-dropzone-title">Click or drag image to upload</p>
                                <p className="ufp-dropzone-hint">PNG, JPG up to 10MB</p>
                            </div>
                        )}
                    </Upload.Dragger>
                )}
            </div>

            <div className="ufp-section">
                <p className="ufp-section-label">Floor Plan Label</p>
                <Input
                    size="large"
                    placeholder="e.g. 2 BHK — Type A"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="ufp-label-input"
                />
            </div>

            <div className="ufp-footer">
                <Button size="large" onClick={onClose} disabled={submitting} className="ufp-cancel-btn">
                    Cancel
                </Button>
                <Button
                    type="primary"
                    size="large"
                    icon={<SaveOutlined />}
                    loading={submitting}
                    disabled={uploading || !currentUrl}
                    onClick={handleSubmit}
                    className="ufp-save-btn"
                >
                    Save Changes
                </Button>
            </div>
        </Modal>
    );
};

export default UpdateFloorPlanPopup;